import { BoundingBox } from '../../../public-map/domain/valueObjects/BoundingBox';
import { Property } from '../../domain/entities/Property';
import { PropertyRepository } from '../../domain/ports/PropertyRepository';
import { api } from '../../../../api/configs/axiosConfigs';
import { PropertyDetail } from '../../domain/entities/PropertyDetail';
import { propertyDtoToDomain } from '../../application/mappers/propertyMapper';
import { Filter } from '../../../public-filters/domain/valueObjects/Filter';
import { MapMarker } from '../../../public-map/domain/entities/MapMarker';
import { PropertySearchRequest } from '../../../public-map/domain/valueObjects/PropertySearchRequest';

// Shared abort controller for cancelling in-flight requests
let abortController: AbortController | null = null;

// this is the implementation of the interface
// Knows how to actually get the data: call /properties via HTTP, map JSON → domain objects.
// Lives in the infrastructure layer.
export class ApiPropertyRepository implements PropertyRepository {
  constructor() {}

  async findByBounds(bounds: BoundingBox): Promise<Property[]> {
    // Cancel any pending requests
    if (abortController) {
      abortController.abort();
    }
    // Create new controller for this request
    abortController = new AbortController();
    try {
      const response = await api.post(
        '/public/properties_in_bounds',
        {
          bounds,
        },
        { signal: abortController.signal }
      );
      return response.data.map(propertyDtoToDomain).filter((p) => p !== null);
    } catch (error) {
      console.error('Error fetching properties in bounds:', error);
      throw error;
    }
  }

  async findInBoundsWithFilters(
    bounds: BoundingBox,
    filters: any
  ): Promise<Property[]> {
    // Cancel any pending requests
    if (abortController) {
      abortController.abort();
    }
    // Create new controller for this request
    abortController = new AbortController();

    try {
      const response = await api.post(
        `/public/properties_in_bounds?bounds=${bounds.toString()}&filters=${filters}`,
        { signal: abortController.signal }
      );
      return response.data.map(propertyDtoToDomain).filter((p) => p !== null);
    } catch (error) {
      console.error('Error fetching properties in bounds with filters:', error);
      throw error;
    }
  }

  async findPropertiesInPolygon(
    polygons: GeoJSON.Feature<GeoJSON.Polygon>[]
  ): Promise<Property[]> {
    // Cancel any pending requests
    if (abortController) {
      abortController.abort();
    }
    // Create new controller for this request
    abortController = new AbortController();

    try {
      const response = await api.post(
        '/public/properties_in_polygon',
        {
          polygons: polygons,
        },
        { signal: abortController.signal }
      );

      return response.data.map(propertyDtoToDomain).filter((p) => p !== null);
    } catch (error) {
      console.error('Error fetching properties in polygon:', error);
      throw error;
    }
  }

  async findById(id: string): Promise<PropertyDetail> {
    const response = await api.get(`/public/property_detail/${id}`);
    return PropertyDetail.fromDetailDTO(response.data);
  }

  async propertySearchRequest(
    searchRequest: PropertySearchRequest
  ): Promise<Property[]> {
    // Cancel any pending requests
    if (abortController) {
      abortController.abort();
    }
    // Create new controller for this request
    abortController = new AbortController();

    try {
      const response = await api.post('public/search', searchRequest, {
        signal: abortController.signal,
      });
      return response.data.map(propertyDtoToDomain).filter((p) => p !== null);
    } catch (error) {
      console.error('Error fetching properties in bounds:', error);
      throw error;
    }
  }
}
